import React, { useContext, useEffect, useRef, useState } from 'react';

import { classNames } from '../../utils/style';
import { DefaultBsComponent } from '../../types/module';
import { debounce } from '../../utils/utils';

import { DropdownContext } from './DropdownContext';

export interface DropdownContentType extends DefaultBsComponent {
  position?: 'top' | 'bottom';
  alignment?: 'left' | 'right';
}

export const DropdownContent = ({
  children,
  position,
  alignment,
  className,
}: DropdownContentType) => {
  const [_position, setPosition] = useState('top');
  const [_alignment, setAlignment] = useState('left');
  const context = useContext(DropdownContext);
  const refContent = useRef<HTMLDivElement | null>(null);
  const height = refContent?.current ? refContent?.current?.offsetHeight : 0;
  const twTranslate = _position === 'top' ? `translate-y-8` : `-translate-y-8`;

  useEffect(() => {
    if (alignment) {
      setAlignment(alignment);
    }
    if (position) {
      setPosition(position);
    }
  }, [alignment, setAlignment, position, setPosition]);

  useEffect(() => {
    const autoChangeAlignment = () => {
      const bounding = refContent?.current?.getBoundingClientRect();

      if (bounding) {
        const overScreenHorizontal =
          bounding?.right > window.innerWidth || bounding?.left < 0;

        if (overScreenHorizontal) {
          if (_alignment === 'left') {
            setAlignment('right');
          }
          if (_alignment === 'right') {
            setAlignment('left');
          }
        }
      }
    };

    const autoChangePosition = () => {
      const bounding = refContent?.current?.getBoundingClientRect();

      if (bounding) {
        const overScreenTop = bounding?.top < 0;
        const overScreenBottom = bounding?.bottom > document.body.offsetHeight;

        if (_position === 'top' && overScreenTop) {
          setPosition('bottom');
        }
        if (_position === 'bottom' && overScreenBottom) {
          setPosition('top');
        }
      }
    };

    const handleClickOutside = (event: any) => {
      if (context.setOpen && !context?.refDropdown?.contains(event.target)) {
        context.setOpen(false);
      }
    };

    const debounceAutoAlign = debounce(autoChangeAlignment, 1000);

    autoChangeAlignment();
    autoChangePosition();

    window.addEventListener('resize', debounceAutoAlign);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('resize', debounceAutoAlign);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    position,
    _position,
    setPosition,
    alignment,
    _alignment,
    setAlignment,
    context,
  ]);

  return (
    <div
      role="menu"
      ref={refContent}
      tabIndex={0}
      className={classNames(
        'DropdownContent',
        className,
        'flex flex-col absolute z-50 opacity-0 invisible overflow-hidden',
        'rounded-[6px] py-8 shadow-btn border border-lang-21',
        'transition ease-out duration-[200ms] bg-white',
        _position,
        _alignment,
        twTranslate
      )}
      style={{
        ...(_alignment === 'left' ? { left: 0 } : { right: 0 }),
        ...(_position === 'top'
          ? { top: `-${height + 10}px` }
          : { marginTop: '10px' }),
        visibility: context.open ? 'visible' : 'hidden',
        opacity: context.open ? 1 : 0,
        transform: context.open ? 'translateY(0px)' : '',
      }}
      aria-activedescendant={context.activedescendant}
    >
      {children}
    </div>
  );
};
